import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PageType, Product, RetailPageType } from '../common';
import { getPageBasedOnPath } from '../utils';
import { getRetailMarketFromFilters } from '../utils/getRetailMarketFromFilters';

const PageTypeMap = {
  [PageType.OPPORTUNITY_REVIEW]: RetailPageType.OPPORTUNITY_REVIEW,
  [PageType.PERFORMANCE_ANALYSIS]: RetailPageType.OPPORTUNITY_REVIEW,
  [PageType.PRODUCT_ANALYZER]: RetailPageType.RI_PRODUCT,
};

type ReturnType = {
  market?: { itemGroupId: string; countryId: string };
};

const SESSION_INTERVAL = 1000; // 1 second
const APPLIED_FILTERS_SESSION_KEY = 'ri_storeFilterSettings';

export const useRetailMarket = (products: Product[] = []): ReturnType => {
  const pathname = window.location.pathname;
  const [itemGroupId, setItemGroupId] = useState('');
  const [countryId, setCountryId] = useState('');
  const { user, isAuthenticated } = useAuthentication();
  const checkSessionInterval = useRef<NodeJS.Timeout>();
  const isRetailProduct = useMemo(() => products.includes(Product.Retail), [products]);
  const pageType = useMemo(() => getPageBasedOnPath(pathname, products)?.type, [pathname, products]);

  const updateRetailMarket = useCallback(() => {
    const appliedFiltersData = JSON.parse(sessionStorage.getItem(APPLIED_FILTERS_SESSION_KEY));
    if (appliedFiltersData !== null && PageTypeMap[pageType]) {
      const { itemGroup, country } = getRetailMarketFromFilters(
        appliedFiltersData.appliedFilters ?? [],
        PageTypeMap[pageType]
      );
      setItemGroupId(itemGroup);
      setCountryId(country);
    }
  }, [pageType]);

  const checkSession = useCallback(() => {
    if (checkSessionInterval.current) {
      clearInterval(checkSessionInterval.current);
    }

    checkSessionInterval.current = setInterval(() => {
      updateRetailMarket();
    }, SESSION_INTERVAL);
  }, [updateRetailMarket]);

  useEffect(() => {
    const sessionIntervalId = checkSessionInterval.current;

    if (!isAuthenticated || !user?.sub || !isRetailProduct) {
      if (sessionIntervalId) {
        clearInterval(sessionIntervalId);
      }
      return;
    }
    checkSession();

    return () => {
      if (sessionIntervalId) {
        clearInterval(sessionIntervalId);
      }
    };
  }, [checkSession, isAuthenticated, isRetailProduct, user?.sub]);

  return useMemo(() => ({ market: { itemGroupId, countryId } }), [itemGroupId, countryId]);
};
